<template>
    <modal
        :active="true"
        @closed="$emit('closed', true)"
        title="Søg serienummer"
        large
    >
        <div class="search-items">
            <div class="columns">
                <div class="column">
                    <FormField
                        label="Søg"
                    >
                        <InputText
                            v-model="searchterm"
                            placeholder="Søg efter serienummer"
                            @input="onSearch"
                            v-focus
                            @keyup.enter="onSearch"
                        />
                    </FormField>
                </div>
            </div>
            <div class="columns">
                <div class="column" v-show="hasPerformedFirstSearch">
                    <b-table :data="results"
                            :sticky-header="true"
                            detailed
                            detail-transition="fade"
                            :show-detail-icon="false"
                            :loading="isLoading"
                            :striped="true"
                            detail-key="id"
                            default-sort="status.for_sale"
                    >
                        <template #empty>
                            <p>Der blev ikke fundet nogen serienummer.</p>
                            <div class="mt-2">
                                <button class="button is-info is-outlined" @click.prevent="createSerialModalOpen = true">Opret serienummer</button>
                            </div>
                        </template>

                        <b-table-column label="Varetype" v-slot="{ row }" sortable field="item.model">
                            <span class="is-clickable">{{ row.item.model }}</span>
                        </b-table-column>

                        <b-table-column label="Serienummer" v-slot="{ row }" sortable field="serial">
                            <span v-if="row.is_ghost">Intet serienummer</span>
                            <span v-else class="is-clickable">
                                    {{ row.serial }}
                            </span>
                        </b-table-column>

                        <b-table-column label="Lokation" v-slot="{ row }" sortable field="location.full_name">
                            <span v-if="row.location">{{ row.location.full_name }}</span>
                            <span v-else>-</span>
                        </b-table-column>

                        <b-table-column label="Status" v-slot="{ row }" sortable field="status.sold">
                            <span>{{ row.status.name }}</span>
                        </b-table-column>

                        <b-table-column label="" v-slot="{ row }">
                            <template v-if="row.status.key === 'sold'">
                                <button :disabled="isSelectButtonDisabled" @click="onSelectSerial(row)" class="button is-primary is-fullwidth">
                                    Vælg
                                </button>
                            </template>
                            <template v-else>
                                <span><em>Ikke solgt</em></span>
                            </template>
                        </b-table-column>

                        <template #detail="props">
                            <div v-if="props.row.type.key === 'with_serial'">
                                <b-table :data="getForSaleSerials(props.row.serials)">
                                    <b-table-column label="Serienr." v-slot="rowData" :sticky-header="false">
                                    <span v-if="rowData.row.is_ghost">
                                        <em>Intet serienummer</em>
                                    </span>
                                        <span v-else>
                                        {{ rowData.row.serial }}
                                    </span>
                                    </b-table-column>
                                    <b-table-column label="Lokation" v-slot="rowData">
                                <span>
                                    {{ rowData.row.location.full_name }}
                                </span>
                                    </b-table-column>
                                    <b-table-column v-slot="rowData">
                                        <div class="has-text-right">
                                            <button class="button"
                                                    @click="onSelectItemWithSerial(props.row, rowData.row)">
                                                Vælg
                                            </button>
                                        </div>
                                    </b-table-column>
                                </b-table>
                            </div>
                        </template>
                    </b-table>
                </div>
            </div>
        </div>

        <SerialModal
            :serial="null"
            v-if="createSerialModalOpen"
            :active="createSerialModalOpen"
            default-status-key="on_return_list"
            :editable-status="false"
            :showAdminFields="false"
            :is-admin="$store.getters.isAdmin"
            @closed="createSerialModalOpen = false"
            @updated="onSerialAdded"
        />
    </modal>
</template>

<script>
import debounce from 'lodash/debounce'
import FormField from "../../../elements/form/FormField";
import InputText from "../../../elements/form/InputText";
import Modal from '@elems/modal'
import ItemPreview from "./ItemPreview";
import SerialModal from "../../serials/forms/SerialModal";
import {mapGetters} from "vuex";

export default {
    components: { SerialModal, ItemPreview, InputText, FormField, Modal },
    props: {
        active: Boolean,
        forProject: {
            type: Boolean,
            default: true
        },
        parent: Object,
    },
    data() {
        return {
            searchterm: '',
            results: [],
            error: {},
            isSelectButtonDisabled: false,
            isLoading: false,
            hasPerformedFirstSearch: false,
            createSerialModalOpen: false,
        };
    },
    computed: {
        apiUrl() {
            const part = this.forProject ? 'project' : 'report'
            return `/api/${part}s/${this.parent.id}/return-item`
        },
    },
    methods: {
        onSerialAdded(serial) {
            this.onSelectSerial(serial);
        },
        onSearch: debounce(async function () {
            this.isLoading = true;
            this.hasPerformedFirstSearch = true;
            try {
                if (!this.searchterm) {
                    this.results = [];
                } else {
                    const queryRelations = '&with=location,status,item';
                    const payload = await window.axios.get(`/api/serials?q=${this.searchterm}${queryRelations}`)
                    this.results = payload.data.data;
                }
                this.isLoading = false;
            } catch (e) {
                this.isLoading = false;
            }
        }, 200),
        onSelectSerial(item) {
            this.isSelectButtonDisabled = true;
            const requestPayload = {
                serial_id: item.id,
                deliver_at: '',
            }

            window.axios.post(`${this.apiUrl}`, requestPayload)
                .then(({ data }) => {
                    this.$emit('completed', data)
                    this.$store.dispatch('setSuccess', 'Linjen blev oprettet')
                    this.isSelectButtonDisabled = false;
                    this.$emit('closed')
                })
                .catch(error => {
                    console.log(error)
                    if (error.response && error.response.status === 422) {
                        this.error = error.response.data.errors
                    }
                    this.isSelectButtonDisabled = false;
                })
        },
    },
}
</script>

<style lang="scss">
.b-table {
    width: 100%;
}

.table-wrapper.has-mobile-cards.has-sticky-header {
    height: 400px;
}
</style>
